//dependencies
import * as Sentry from "@sentry/react";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { z } from "zod";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { faCircleCheck, faClone } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { toast } from "@performyard/py-components";

// components
import { AdminContainer, AdminTitle } from "../../../../components/Admin";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Button from "../../../../components/Button";
import { CopyToClipboard } from "../../../../components/CopyToClipboard";
import { Grid, GridCol } from "../../../../components/Grid";
import Input from "../../../../components/Inputs/InputText";
import Toggle from "../../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import ConfirmationModal from "../../../../components/Modal/Templates/ConfirmationModal";
import RevealContent from "../../../../components/RevealContent";
import Spacer from "../../../../components/Spacer";
import Spinner from "../../../../components/Spinner/ComponentLoader";
import Text from "../../../../components/Text";
import Tooltip from "../../../../components/Tooltip";

//hooks
import { useGetScimTokenById } from "../../../../api/hooks";
import useForm from "../../../../hooks/useForm";

//api
import { regenerateScimToken, updateScimToken } from "../../../../api/services";

import css from "./ViewSCIMToken.module.scss";

const formSchema = z.object({
	name: z.string().min(1).max(100),
	scim_token: z.string(),
	manager_sync_enabled: z.boolean(),
});

const CopyButton = (props: { copyValue: string }) => {
	const { copyValue } = props;
	const [copied, setCopied] = useState(false);

	const handleCopy = () => {
		setCopied(true);
		setTimeout(() => {
			setCopied(false);
		}, 2000);
	};

	return (
		<div className={css.copyButtonContainer}>
			<Tooltip message="Copied" placement="top" openOnClick hasArrow={false} turnOffAfter={2000}>
				<CopyToClipboard text={copyValue} onCopy={handleCopy}>
					{copied ? (
						<FontAwesomeIcon icon={faCircleCheck} className={css.copySuccess} />
					) : (
						<FontAwesomeIcon icon={faClone} className={css.copyButton} />
					)}
				</CopyToClipboard>
			</Tooltip>
		</div>
	);
};

const ViewSCIMToken = () => {
	// get id from url
	const { scimId } = useParams();

	const { data: resp, refetch, isLoading } = useGetScimTokenById(scimId || "");

	const { state, setInitialState, updateForm, validate, resetForm, setNewDefaultValues } = useForm({
		schema: formSchema,
		disableReactWarning: true,
	});

	const [hasChanges, setHasChanges] = React.useState(false);
	const [recycleModal, setRecycleModal] = useState<null | { id: string; name: string; enabled: boolean }>(null);

	const validation = useMemo(() => validate(), [state]);

	const hostname = window.location.hostname === "localhost" ? "localhost:3000" : `im.${window.location.hostname}`;
	const tenant_url = `https://${hostname}/scim/v2`;

	const refetchAndUpdateForm = async () => {
		const newResp = await refetch();
		if (newResp?.data?.data) {
			setNewDefaultValues({
				name: newResp.data.data.name,
				scim_token: newResp.data.data.token,
				manager_sync_enabled: newResp.data.data.manager_sync_enabled,
			});
		}
	};

	const editMutation = useMutation({
		mutationFn: updateScimToken,
		onSuccess: async () => {
			await refetchAndUpdateForm();
			setHasChanges(false);

			toast.success("Your SCIM settings have been updated.");
		},
		onError: () => {
			toast.error("Unable to update your SCIM settings");
		},
	});

	const handleChange = (name, value) => {
		if (state[name] !== value) {
			setHasChanges(true);
		}

		updateForm({
			[name]: value,
		});
	};

	const handleReset = () => {
		setHasChanges(false);
		resetForm();
	};

	useEffect(() => {
		if (!resp) return;

		setInitialState({
			name: resp.data.name,
			scim_token: resp.data.token,
			manager_sync_enabled: resp.data.manager_sync_enabled,
		});
	});

	const handleRecycle = async () => {
		if (!recycleModal) return;
		await regenerateScimToken({ _id: recycleModal.id, name: recycleModal.name, enabled: recycleModal.enabled });
		setRecycleModal(null);
		refetch();
		toast.success("A new Secret Token has been created.");
	};

	const handleCancelModal = () => {
		setRecycleModal(null);
	};

	const modal = recycleModal ? (
		<ConfirmationModal
			positiveAction={handleRecycle}
			negativeAction={handleCancelModal}
			positiveActionLabel="Yes"
			negativeActionLabel="Cancel"
		>
			<>
				Are you sure you want to recycle token &quot;
				<Text element="span" size="paragraph-2" weight="semi-bold">
					{recycleModal.name}
				</Text>
				&quot;?
			</>
		</ConfirmationModal>
	) : null;

	if (isLoading) {
		return <Spinner size={30} />;
	}

	return (
		<>
			{modal}
			<Sentry.ErrorBoundary fallback={undefined}>
				<AdminContainer>
					<div className={css.main}>
						{/* Title andBreadCrumbs */}
						<Grid cols={1}>
							<GridCol span={1}>
								<AdminTitle includeSpacer={false}>SCIM</AdminTitle>
								<Spacer height={2} />
								<div>
									<Breadcrumbs
										pages={
											resp?.data.name
												? [
														{
															pageID: "scim-api",
															label: "SCIM",
															url: "/administration/scim",
														},
														{
															pageID: "scim-api-edit",
															label: resp.data.name,
															url: `/administration/scim/${resp.data._id || ""}`,
														},
													]
												: []
										}
									/>
								</div>
								<Spacer height={4} />
							</GridCol>
						</Grid>

						{/* Content */}
						<Grid cols={2}>
							{/* Left Column */}
							<GridCol width={300}>
								{/* SCIM Token Name */}
								<Input
									value={state.name}
									label="Name"
									onChange={(value) => handleChange("name", value)}
									maxLength={100}
									errorMessage={validation.errors.name?.message ?? undefined}
									testId="scim-token-name-input"
								/>

								<Spacer height={2} />
								{/* SCIM Token Controls */}
								<Grid cols={2} gap={1}>
									{hasChanges && (
										<GridCol width={"88px"}>
											<Button isHollow={true} size="large" onClick={handleReset}>
												Discard
											</Button>
										</GridCol>
									)}

									<GridCol width={"128px"}>
										<Button
											color="green"
											size="large"
											disabled={!hasChanges || state.name === ""}
											onClick={() => {
												if (resp?.data?._id) {
													editMutation.mutate({
														_id: resp?.data?._id,
														name: state.name,
														enabled: resp?.data.enabled || false, // added default to false
														manager_sync_enabled: resp.data.manager_sync_enabled,
													});
												}
											}}
											isRequesting={editMutation.isPending}
										>
											Save Changes
										</Button>
									</GridCol>
								</Grid>
							</GridCol>
							{/* Gap */}
							<GridCol width="1fr" />
							{/* Right Column */}
							<GridCol width={456}>
								{/* SCIM Manager Sync Toggle */}
								<Grid cols={2} gap={1} className={css.syncManagerSettingContainer}>
									<GridCol width="1fr">
										<Grid cols={2} gap={0}>
											<GridCol width="213px">
												<Text size="paragraph-3" weight="semi-bold">
													Manager Sync Assignment via SCIM
												</Text>
											</GridCol>
											<GridCol width="12px" className={css.tooltipIcon}>
												<Tooltip
													message="This setting determines whether the Manager field is locked and managed solely by SCIM or unlocked and available for editing by any appropriate users or systems."
													placement="top"
													hasArrow={true}
												>
													<FontAwesomeIcon icon={faCircleInfo} data-testid="circle-info" />
												</Tooltip>
											</GridCol>
										</Grid>
									</GridCol>
									<GridCol width="48px">
										<Toggle
											value={state.manager_sync_enabled}
											onChange={(newValue) => {
												handleChange("manager_sync_enabled", newValue);
												if (resp?.data?._id) {
													editMutation.mutate({
														_id: resp.data._id,
														name: resp.data.name,
														enabled: resp.data.enabled || false, // added default to false
														manager_sync_enabled: newValue,
													});
												}
											}}
										/>
									</GridCol>
								</Grid>

								<Spacer height={2} />

								{/* Tenant URL */}
								<div>
									<Grid cols={1} gap={1}>
										<GridCol width="16px">
											<CopyButton copyValue={tenant_url} />
										</GridCol>
										<GridCol width="1fr">
											<Text
												element="label"
												size="paragraph-3"
												weight="semi-bold"
												htmlFor="tenantUrlInput"
											>
												Tenant URL
											</Text>
										</GridCol>
									</Grid>

									<Spacer height={1} />

									{/* <RevealContent label="Reveal Secret Token"> */}
									<Input inputId="tenantUrlInput" value={tenant_url} disabled={true} />
									{/* </RevealContent> */}
								</div>

								<Spacer height={2} />

								{/* SCIM Token */}
								<div>
									<Grid cols={1} gap={1}>
										<GridCol width="16px">
											<CopyButton copyValue={resp?.data.token || ""} />{" "}
											{/*added default to empty string*/}
										</GridCol>
										<GridCol width="1fr">
											<Text
												element="label"
												size="paragraph-3"
												weight="semi-bold"
												htmlFor="scimTokenInput"
											>
												Secret Token
											</Text>
										</GridCol>
									</Grid>

									<Spacer height={1} />

									<RevealContent label="Reveal Token">
										<Input
											inputId="scimTokenInput"
											value={resp?.data.token || ""} // added default to empty string
											disabled={true}
										/>
									</RevealContent>
								</div>

								<Spacer height={2} />

								{/* Recycle Button */}
								<div>
									<Button
										size="large"
										color="blue"
										onClick={() => {
											if (resp?.data) {
												setRecycleModal({
													id: resp?.data._id,
													name: resp?.data.name,
													enabled: resp?.data.enabled,
												});
											}
										}}
									>
										Recycle Token
									</Button>
									<Spacer height={2} />
								</div>
							</GridCol>
						</Grid>
					</div>
				</AdminContainer>
			</Sentry.ErrorBoundary>
		</>
	);
};

export default ViewSCIMToken;
